import React from 'react';
import { ANIMATIONS, PALETTES } from '@/types/user';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMutation } from '@tanstack/react-query';
import { patchLocale } from '@/services/user';
import i18next from '@/i18next';
import { toast } from 'sonner';
import { cn, errorHandler } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/providers/theme-provider';
import { useThemeMutation } from '@/hooks/use-theme-mutation';
import { usePaletteMutation } from '@/hooks/use-palette-mutation';
import { useAnimationMutation } from '@/hooks/use-animation-mutation';
import { Check, Languages, Monitor, Moon, Palette as PaletteIcon, Sparkles, Sun } from 'lucide-react';
// Hex preview swatches — quick visual cue for the picker without
// reaching into the CSS palette tokens. Order matches PALETTES.
const PALETTE_SWATCHES = {
    default: ['#7C5CDA', '#A88EE8'],
    polaroid: ['#B8A3DD', '#F4E5C2'],
    editorial: ['#1A1A1A', '#F5F5F0'],
    claymorphism: ['#FFB8D9', '#A0E7E5'],
    botanical: ['#6B7C5C', '#C3D2A8'],
    bauhaus: ['#E63946', '#FFD166'],
    brutalist: ['#0A0A0A', '#FAFAFA'],
    risograph: ['#FF5733', '#1C4FFB'],
    cyber: ['#00F0FF', '#FF00C8'],
    y2k: ['#FFB8E0', '#9FE7FF'],
    terminal: ['#33FF66', '#0A0A0A'],
};
// Animation chip background — mimics each pattern just enough to be recognisable
// at thumbnail size. Real previews would mount the full pattern; not worth it here.
const ANIMATION_PREVIEW = {
    none: 'bg-muted',
    dust: 'bg-gradient-to-br from-amber-100/60 to-amber-200/30 dark:from-amber-900/30 dark:to-amber-950/40',
    petals: 'bg-gradient-to-br from-pink-200/60 to-rose-300/40 dark:from-pink-900/40 dark:to-rose-950/30',
    orbits: 'bg-[radial-gradient(circle_at_30%_30%,#8b5cf6_2px,transparent_2px),radial-gradient(circle_at_70%_70%,#06b6d4_2px,transparent_2px)] bg-[length:24px_24px] bg-slate-900/5 dark:bg-slate-900/40',
    clay_blobs: 'bg-gradient-to-br from-pink-200 to-cyan-200 dark:from-pink-900/50 dark:to-cyan-900/50',
    paper_grain: 'bg-amber-50 dark:bg-amber-950/20 bg-[radial-gradient(#000_1px,transparent_1px)] bg-[length:4px_4px]',
    marquee: 'bg-[repeating-linear-gradient(45deg,#ef4444_0,#ef4444_8px,#fff_8px,#fff_16px)] dark:bg-[repeating-linear-gradient(45deg,#ef4444_0,#ef4444_8px,#000_8px,#000_16px)]',
    ink_blobs: 'bg-gradient-to-br from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300',
    neon_grid: 'bg-slate-950 bg-[linear-gradient(#06b6d4_1px,transparent_1px),linear-gradient(90deg,#06b6d4_1px,transparent_1px)] bg-[length:12px_12px]',
    matrix_rain: 'bg-black text-emerald-400 font-mono text-[8px] leading-[8px] overflow-hidden',
    holo_blobs: 'bg-[conic-gradient(from_0deg,#a855f7,#ec4899,#06b6d4,#a855f7)]',
};
const THEMES = [
    { value: 'light', icon: Sun, key: 'light' },
    { value: 'dark', icon: Moon, key: 'dark' },
    { value: 'system', icon: Monitor, key: 'system' },
];
const LOCALES = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'km', label: 'ភាសាខ្មែរ' },
];
export const PreferencesCard = ({ user }) => {
    const { t } = useTranslation(['profile', 'common']);
    const { theme, palette, animation } = useTheme();
    const { updateTheme } = useThemeMutation();
    const { updatePalette } = usePaletteMutation();
    const { updateAnimation } = useAnimationMutation();
    const { mutate: mutateLocale } = useMutation({
        mutationFn: patchLocale,
        onSuccess: (data) => {
            const { message, user } = data;
            i18next.changeLanguage(user.locale).then(() => toast.success(message));
        },
        onError: errorHandler,
    });
    return (<>
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Languages className="h-5 w-5"/>
                        {t('preferences.locale', { defaultValue: 'Locale' })}
                    </CardTitle>
                    <CardDescription>
                        {t('preferences.locale_desc', {
            defaultValue: 'The language we use across the console and emails.',
        })}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t('preferences.language')}</label>
                        <Select defaultValue={user.locale} onValueChange={(locale) => mutateLocale({ locale })}>
                            <SelectTrigger className="w-60">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {LOCALES.map((l) => (<SelectItem key={l.value} value={l.value}>
                                        {l.label}
                                    </SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <PaletteIcon className="h-5 w-5"/>
                        {t('preferences.appearance', { defaultValue: 'Appearance' })}
                    </CardTitle>
                    <CardDescription>
                        {t('preferences.appearance_desc', {
            defaultValue: 'Theme, palette, and ambient motion. Changes apply instantly across the console.',
        })}
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                    <div className="space-y-3">
                        <label className="text-sm font-medium">{t('preferences.theme')}</label>
                        <div role="radiogroup" aria-label={t('preferences.theme')} className="inline-flex rounded-md border bg-muted/40 p-1">
                            {THEMES.map(({ value, icon: Icon, key }) => {
            const active = theme === value;
            return (<button key={value} type="button" role="radio" aria-checked={active} onClick={() => updateTheme(value)} className={cn('inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors', active
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground')}>
                                        <Icon className="h-4 w-4"/>
                                        {t(key, { ns: 'common' })}
                                    </button>);
        })}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <label className="text-sm font-medium">{t('preferences.palette')}</label>
                        <div role="radiogroup" aria-label={t('preferences.palette')} className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                            {PALETTES.map((p) => {
            const [c1, c2] = PALETTE_SWATCHES[p];
            const active = palette === p;
            return (<button key={p} type="button" role="radio" aria-checked={active} onClick={() => updatePalette(p)} className={cn('group flex flex-col items-center gap-1.5 rounded-lg border p-2 transition-all', active
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-border hover:border-primary/50')}>
                                        <div className="relative h-10 w-full overflow-hidden rounded-md" style={{
                    background: `linear-gradient(135deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`,
                }}>
                                            {active && (<span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-background shadow">
                                                    <Check className="h-3 w-3 text-primary"/>
                                                </span>)}
                                        </div>
                                        <span className="text-[11px] font-medium text-center leading-tight">
                                            {t(`palettes.${p}`)}
                                        </span>
                                    </button>);
        })}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium flex items-center gap-1.5">
                                <Sparkles className="h-3.5 w-3.5"/>
                                {t('preferences.animation')}
                            </label>
                            <span className="text-xs text-muted-foreground">
                                {t('preferences.animation_help_short', {
            defaultValue: 'Disabled automatically when your OS asks for reduced motion.',
        })}
                            </span>
                        </div>
                        <div role="radiogroup" aria-label={t('preferences.animation')} className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                            {ANIMATIONS.map((a) => {
            const active = animation === a;
            return (<button key={a} type="button" role="radio" aria-checked={active} onClick={() => updateAnimation(a)} className={cn('group flex flex-col items-center gap-1.5 rounded-lg border p-2 transition-all', active
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-border hover:border-primary/50')}>
                                        <div className={cn('relative h-10 w-full overflow-hidden rounded-md', ANIMATION_PREVIEW[a])}>
                                            {active && (<span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-background shadow">
                                                    <Check className="h-3 w-3 text-primary"/>
                                                </span>)}
                                        </div>
                                        <span className="text-[11px] font-medium text-center leading-tight">
                                            {t(`animations.${a}`)}
                                        </span>
                                    </button>);
        })}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </>);
};
PreferencesCard.displayName = 'PreferencesCard';
